import React, { FC, useCallback, useMemo } from 'react';
import { Flex, NoSsr } from '@withjoy/joykit';
import { addRendition } from '@shared/utils/photoRendition';
import { useEventInfo } from '@shared/utils/eventInfo';
import { GuestMediaGalleryComponentFragment } from '@graphql/generated';
import { PhotoGallery as PhotoGalleryView } from '@apps/guest/routes/MediaCollection/routes/ViewAlbum/components/PhotoGallery/PhotoGallery';
import { Photo } from '@apps/guest/routes/MediaCollection/routes/ViewAlbum/components/PhotoGallery/PhotoGallery.types';
import { useCustomPageTelemetry } from '../../Custom.telemetry';

interface PhotoGalleryProps {
  mediaGalleryComponentData: GuestMediaGalleryComponentFragment;
}

export const PhotoGallery: FC<PhotoGalleryProps> = props => {
  const { mediaGalleryComponentData } = props;

  const { eventInfo } = useEventInfo();

  const telemetry = useCustomPageTelemetry();

  const photos: Photo[] = useMemo(
    () =>
      (mediaGalleryComponentData.mediaCollection?.media
        ?.map(media => {
          if (media.__typename === 'MediaPhoto') {
            return {
              mediaId: media.id,
              id: media.photo.id,
              src: addRendition({ url: media.photo.url, renditionSize: 'medium' }),
              width: media.photo.width,
              height: media.photo.height
            };
          }
          return null;
        })
        .filter(media => media !== null) as Photo[]) || [],
    [mediaGalleryComponentData.mediaCollection?.media]
  );

  const handlePhotoClick = useCallback(
    (mediaId: string, photoUrl: string) => {
      telemetry.photoCardClicked(eventInfo?.eventId || '', mediaGalleryComponentData.mediaCollection?.id || '', mediaId, photoUrl);
    },
    [eventInfo?.eventId, mediaGalleryComponentData.mediaCollection?.id, telemetry]
  );

  return (
    <>
      {photos.length > 0 && (
        <NoSsr>
          <Flex width="100%" flexDirection="column">
            <PhotoGalleryView
              key={mediaGalleryComponentData.id}
              photos={photos}
              layout={photos.length > 8 ? 'masonry' : 'columns'}
              columns={3}
              spacing={2}
              padding={0}
              onPhotoClick={handlePhotoClick}
            />
          </Flex>
        </NoSsr>
      )}
    </>
  );
};

PhotoGallery.displayName = 'PhotoGallery';
